import React, { useEffect, useState } from 'react';
import {
    Box, ContentLayout, Header, SpaceBetween,
    StatusIndicator, Spinner, TreeView,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { alphaAnalysisApi } from '../../client/clients';
import { SectorSummary } from '../../api';

interface SectorNode {
    id: string;
    label: string;
    fullSector: string | null;
    data: SectorSummary | null;
    children: SectorNode[];
}

function parseParts(sector: string): string[] {
    return sector.split(/ \/ | - /).map(s => s.trim()).filter(Boolean);
}

function buildTree(sectors: SectorSummary[]): SectorNode[] {
    const nodeMap = new Map<string, SectorNode>();

    for (const s of sectors) {
        const parts = parseParts(s.sector ?? '');
        for (let depth = 1; depth <= parts.length; depth++) {
            const key = parts.slice(0, depth).join('\x00');
            if (!nodeMap.has(key)) {
                nodeMap.set(key, { id: key, label: parts[depth - 1], fullSector: null, data: null, children: [] });
            }
        }
        const leafKey = parts.join('\x00');
        const leaf = nodeMap.get(leafKey)!;
        leaf.fullSector = s.sector ?? null;
        leaf.data = s;
    }

    const roots: SectorNode[] = [];
    for (const [key, node] of nodeMap) {
        const parts = key.split('\x00');
        if (parts.length === 1) {
            roots.push(node);
        } else {
            const parentKey = parts.slice(0, -1).join('\x00');
            const parent = nodeMap.get(parentKey);
            if (parent && !parent.children.includes(node)) {
                parent.children.push(node);
            }
        }
    }

    const sortNodes = (nodes: SectorNode[]): SectorNode[] => {
        nodes.sort((a, b) => a.label.localeCompare(b.label));
        nodes.forEach(n => sortNodes(n.children));
        return nodes;
    };

    return sortNodes(roots);
}

function outlookColor(score: number): 'success' | 'warning' | 'error' {
    if (score >= 20) return 'success';
    if (score >= -20) return 'warning';
    return 'error';
}

export const AlphaSectorOverviewPage: React.FC = () => {
    useDocumentTitle('Alpha Analysis — Sectors');
    const navigate = useNavigate();
    const [sectors, setSectors] = useState<SectorSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    useEffect(() => {
        alphaAnalysisApi.getSectors()
            .then(r => setSectors(r.data))
            .catch(() => setError('Failed to load sector data'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>;
    if (error) return <Box padding="l"><StatusIndicator type="error">{error}</StatusIndicator></Box>;

    const tree = buildTree(sectors);
    // Encode "/" separators as "--" so the URL path stays slash-free.
    // AlphaSectorDetailPage decodes "--" back to " / " before calling the API.
    const onNavigate = (sector: string) =>
        navigate(`/alpha/sectors/${encodeURIComponent(sector.replace(/ \/ /g, '--'))}`);

    return (
        <ContentLayout header={<Header variant="h1">Alpha Analysis — Sectors</Header>}>
            <TreeView
                items={tree}
                ariaLabel="Sector overview"
                getItemId={(node) => node.id}
                getItemChildren={(node) => node.children.length > 0 ? node.children : undefined}
                expandedItems={expandedItems}
                onItemToggle={({ detail }) =>
                    setExpandedItems(prev =>
                        detail.expanded
                            ? [...prev, detail.id]
                            : prev.filter(id => id !== detail.id)
                    )
                }
                renderItem={(node) => {
                    const score = node.data?.avgOutlook30d ?? 0;
                    const count = node.data?.articleCount30d ?? 0;
                    return {
                        announcementLabel: node.label,
                        content: node.fullSector ? (
                            <span
                                style={{ cursor: 'pointer', color: '#0972d3' }}
                                onClick={() => onNavigate(node.fullSector!)}
                            >
                                {node.label}
                            </span>
                        ) : (
                            <span>{node.label}</span>
                        ),
                        secondaryContent: node.data ? (
                            <SpaceBetween direction="horizontal" size="xs">
                                <StatusIndicator type={outlookColor(score)}>
                                    {score > 0 ? '+' : ''}{score.toFixed(1)}
                                </StatusIndicator>
                                <Box color="text-body-secondary" fontSize="body-s">
                                    {count} {count === 1 ? 'article' : 'articles'}
                                </Box>
                            </SpaceBetween>
                        ) : undefined,
                    };
                }}
            />
        </ContentLayout>
    );
};
